import { useEffect, useState } from "react";
import { Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDebounced } from "@/components/DataTable";
import { PageHeader } from "@/components/common";
import { Chip, LEVEL } from "@/components/risk";

export default function CountryRisk() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const dq = useDebounced(q);
  const load = () => api.get("/risk/countries", { params: { q: dq, level } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [dq, level]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try { await api.put(`/risk/countries/${edit.code}`, edit); toast.success(`${edit.name} updated – open assessments recalculated`); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="country-risk-page">
      <PageHeader title="Country risk benchmark" subtitle="EU country benchmarking under Article 29 EUDR – Commission Implementing Regulation (EU) 2025/1093 (in force 26 May 2025; no later amendment published as of Oct 2026). Countries not listed are Standard risk by default." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input data-testid="country-search-input" className="h-9 bg-white pl-9" placeholder="Search country or ISO code…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        {["all", "low", "standard", "high"].map((l) => (
          <button key={l} data-testid={`country-filter-${l}`} onClick={() => setLevel(l)} className={`rounded-full border px-3 py-1 text-xs font-medium ${level === l ? "border-[#D52B3B] bg-[#D52B3B]/10 text-[#D52B3B]" : "border-slate-200 bg-white text-slate-600"}`}>
            {l === "all" ? "All" : LEVEL[l][0]} {l !== "all" && d?.counts?.[l] != null && `(${d.counts[l]})`}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="country-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr>{["ISO", "Country", "Risk", "Source", "Effective", "Updated", ""].map((h) => <th key={h} className="px-4 py-2 text-left">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {d?.items.map((c) => (
              <tr key={c.code} data-testid={`country-row-${c.code}`}>
                <td className="px-4 py-2 font-mono text-xs">{c.code}</td><td className="px-4 py-2 font-medium">{c.name}</td>
                <td className="px-4 py-2"><Chip map={LEVEL} value={c.level} testid={`country-level-${c.code}`} /></td>
                <td className="max-w-md px-4 py-2 text-xs text-slate-500">{c.source}</td><td className="px-4 py-2 text-xs">{c.effective_date}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{c.updated_by} {c.updated_by !== "seed" && fmtDate(c.updated_at)}</td>
                <td className="px-4 py-2">{can.admin && <button data-testid={`country-edit-${c.code}`} onClick={() => setEdit({ ...c })} className="text-slate-400 hover:text-slate-800"><Pencil className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="country-edit-dialog">
          <DialogHeader><DialogTitle>{edit?.name}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <select data-testid="country-level-select" className="h-10 w-full rounded-md border px-2 text-sm" value={edit.level} onChange={(e) => setEdit({ ...edit, level: e.target.value })}>
              <option value="low">Low</option><option value="standard">Standard</option><option value="high">High</option>
            </select>
            <Input data-testid="country-source-input" placeholder="Source (legal act)" value={edit.source} onChange={(e) => setEdit({ ...edit, source: e.target.value })} />
            <Input data-testid="country-date-input" type="date" value={edit.effective_date} onChange={(e) => setEdit({ ...edit, effective_date: e.target.value })} />
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button data-testid="country-save-btn" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
